import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import formService from '../services/formService';
import draftService from '../services/draftService';
import excelService from '../services/excelService';
import authorizationService from '../services/authorizationService';
import authService from '../services/authService';
import standardsService from '../services/standardsService';
import { DYNAMIC_DROPDOWN_FIELDS } from '../config/dynamicDropdownConfig';
import PhotoFieldUpload from '../components/PhotoFieldUpload';
import DynamicRowsField from '../components/DynamicRowsField';

const DynamicForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const { templateId, formType, equipmentType, draftData, selectedInspector } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({});
  // Tracks which option INDEX is displayed as selected per select-type field. Needed because some
  // fields (e.g. "summary") have multiple options sharing identical text/value — a plain HTML
  // <select> always visually highlights the first matching option in that case, so we key options
  // by index and keep this map to show the actual chosen option regardless of duplicate text.
  const [selectDisplayIndex, setSelectDisplayIndex] = useState({});
  const [selectedClientId, setSelectedClientId] = useState('');
  const [inspectionDate, setInspectionDate] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Inspector state (from NewInspectionForm selection)
  const [inspectorInfo, setInspectorInfo] = useState(null);

  // Draft-related states
  const [savingDraft, setSavingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);

  // Excel import states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [importedData, setImportedData] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Copy from work order states
  const [copyWorkOrderNo, setCopyWorkOrderNo] = useState('');
  const [copyingFromWorkOrder, setCopyingFromWorkOrder] = useState(false);

  // Dynamic dropdown options state
  const [dynamicOptions, setDynamicOptions] = useState({});

  useEffect(() => {
    if (!templateId) {
      navigate('/new-inspection');
      return;
    }

    fetchTemplateAndClients();
  }, [templateId]);

  // Due-date field names used across templates
  const DUE_DATE_FIELD_NAMES = ['next_inspection_due_on', 'next_inspection_due', 'date_of_next_examination'];

  // Returns true if the equipment is a rigging accessory (6-month inspection cycle)
  const isAccessoriesEquipment = (equipType) => {
    if (!equipType) return false;
    const name = (equipType.equipment_name || '').toLowerCase();
    const accessoriesKeywords = [
      'wire rope sling', 'chain sling', 'webbing sling', 'shackle',
      'eye bolt', 'beam clamp', 'rigging screw', 'hook',
      'chain block', 'lever block', 'jaw winch', 'beam trolley',
      'plate clamp', 'pulley block', 'sling', 'lifting accessory'
    ];
    return accessoriesKeywords.some(kw => name.includes(kw));
  };

  // Returns next inspection due date string (YYYY-MM-DD) given an inspection date string
  const calculateNextInspectionDate = (inspectionDateStr, isAccessories) => {
    if (!inspectionDateStr) return '';
    const date = new Date(inspectionDateStr);
    if (isNaN(date.getTime())) return '';
    if (isAccessories) {
      date.setMonth(date.getMonth() + 6);
    } else {
      date.setFullYear(date.getFullYear() + 1);
    }
    return date.toISOString().split('T')[0];
  };

  // ADNOC colour code cycle based on odd/even years:
  //   Odd years  (2025, 2027, 2029, ...): Jan–Jun = White,  Jul–Dec = Blue
  //   Even years (2026, 2028, 2030, ...): Jan–Jun = Orange, Jul–Dec = Green
  const COLOUR_CYCLE = ['White', 'Blue', 'Orange', 'Green'];

  const getColourPeriodIndex = (year, isFirstHalf) => {
    const isOddYear = year % 2 !== 0;
    if (isOddYear && isFirstHalf)  return 0; // White
    if (isOddYear && !isFirstHalf) return 1; // Blue
    if (!isOddYear && isFirstHalf) return 2; // Orange
    return 3;                                // Green
  };

  // Returns the full colour code string for the certificate, e.g.:
  // "Blue till December 2025 – Then Orange till the next date of inspection"
  const getAdnocColourCode = (inspectionDateStr) => {
    if (!inspectionDateStr) return '';
    const date = new Date(inspectionDateStr);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const isFirstHalf = date.getMonth() < 6; // Jan(0)–Jun(5) = first half
    const idx = getColourPeriodIndex(year, isFirstHalf);
    const colour = COLOUR_CYCLE[idx];
    const nextColour = COLOUR_CYCLE[(idx + 1) % 4];
    const endMonth = isFirstHalf ? 'June' : 'December';
    return `${colour} till ${endMonth} ${year} – Then ${nextColour} till the next date of inspection`;
  };

  // Returns a human-readable hint: "Blue (Jul 2026 – Dec 2026) → Next: Orange (Jan 2027 – Jun 2027)"
  const getColourCodeHint = (inspectionDateStr) => {
    if (!inspectionDateStr) return '';
    const date = new Date(inspectionDateStr);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const isFirstHalf = date.getMonth() < 6;
    const idx = getColourPeriodIndex(year, isFirstHalf);
    const colour = COLOUR_CYCLE[idx];
    const nextColour = COLOUR_CYCLE[(idx + 1) % 4];

    const periodStart = isFirstHalf ? `Jan ${year}` : `Jul ${year}`;
    const periodEnd   = isFirstHalf ? `Jun ${year}` : `Dec ${year}`;
    const nextStart   = isFirstHalf ? `Jul ${year}` : `Jan ${year + 1}`;
    const nextEnd     = isFirstHalf ? `Dec ${year}` : `Jun ${year + 1}`;

    return `${colour} (${periodStart} – ${periodEnd}) → Next: ${nextColour} (${nextStart} – ${nextEnd})`;
  };

  // Helper function to get default value based on field type and label
  const getDefaultValue = (field) => {
    const fieldLabel = (field.field_label || '').toLowerCase();
    const fieldType = field.field_type;

    // Skip static_text fields
    if (fieldType === 'static_text') {
      return null;
    }

    // equipment_name / asset_unit_name / owner_name are auto-filled elsewhere (from the selected
    // equipment type / client) and made read-only — never use the generic test-data heuristics below.
    if (['equipment_name', 'asset_unit_name', 'owner_name'].includes(field.field_name)) {
      return null;
    }

    // Asset/ID No has no real default — pre-fill "N/A" so it's never left blank, still editable.
    if (field.field_name === 'asset_id_no') {
      return 'N/A';
    }

    // ===== Label-specific defaults (these override type defaults) =====

    // Owner name or address fields
    if (fieldLabel.includes('owner') && (fieldLabel.includes('address') || fieldLabel.includes('location'))) {
      return 'BVRaju, Sagarnagar';
    }

    if (fieldLabel.includes('owner') && fieldLabel.includes('name')) {
      return 'BVRaju';
    }

    if (fieldLabel.includes('address') || fieldLabel.includes('location')) {
      return 'BVRaju, Sagarnagar';
    }

    // Person name fields (inspector, person, name, etc.)
    if (fieldLabel.includes('inspector') || fieldLabel.includes('person') ||
        fieldLabel.includes('name') || fieldLabel.includes('operator') ||
        fieldLabel.includes('supervisor') || fieldLabel.includes('technician') ||
        fieldLabel.includes('engineer') || fieldLabel.includes('manager')) {
      return 'Arun';
    }

    // Serial number or ID fields
    if (fieldLabel.includes('serial') || fieldLabel.includes('id') || fieldLabel.includes('number')) {
      if (fieldType === 'number') {
        return '12345';
      }
      return 'SN-12345';
    }

    // Email fields
    if (fieldType === 'email' || fieldLabel.includes('email') || fieldLabel.includes('e-mail')) {
      return 'test@example.com';
    }

    // URL fields
    if (fieldType === 'url' || fieldLabel.includes('url') || fieldLabel.includes('website')) {
      return 'http://example.com';
    }

    // Phone number fields
    if (fieldLabel.includes('phone') || fieldLabel.includes('mobile') || fieldLabel.includes('contact')) {
      return '9876543210';
    }

    // ===== Field type specific defaults =====

    switch (fieldType) {
      case 'date':
        // Return current date in YYYY-MM-DD format
        return new Date().toISOString().split('T')[0];

      case 'number':
        // Default number value
        return '10';

      case 'text':
        // Default text value
        return 'vizag';

      case 'textarea':
        // Default textarea value
        return 'vizag';

      case 'checkbox':
        // Get all checkbox options and select them all
        try {
          console.log(`[getDefaultValue] Checkbox field: ${field.field_label}`);
          console.log(`  field_options type: ${typeof field.field_options}`);
          console.log(`  field_options raw:`, field.field_options);
          console.log(`  full field object:`, field);

          if (!field.field_options) {
            console.warn(`  field_options is null/undefined for ${field.field_label}`);
            return [];
          }

          const options = typeof field.field_options === 'string'
            ? JSON.parse(field.field_options)
            : (field.field_options || []);

          console.log(`  parsed options:`, options);

          if (!Array.isArray(options) || options.length === 0) {
            console.warn(`  No valid options found for ${field.field_label}`);
            return [];
          }

          const defaultValues = options.map(opt => typeof opt === 'object' ? opt.value : opt);
          console.log(`  default values:`, defaultValues);

          return defaultValues;
        } catch (e) {
          console.error(`[getDefaultValue] Error parsing checkbox options for ${field.field_label}:`, e);
          return [];
        }

      case 'select':
      case 'radio':
        // Get first option as default
        try {
          const options = typeof field.field_options === 'string'
            ? JSON.parse(field.field_options)
            : (field.field_options || []);
          if (options.length > 0) {
            return typeof options[0] === 'object' ? options[0].value : options[0];
          }
        } catch (e) {
          return '';
        }
        return '';

      default:
        // Fallback default for any other field type
        return 'vizag';
    }
  };

  const fetchTemplateAndClients = async () => {
    try {
      setLoading(true);
      setError('');

      // Set inspector info from props (passed from NewInspectionForm) or from draft data
      if (selectedInspector) {
        setInspectorInfo(selectedInspector);
      } else if (draftData && draftData.inspector_id) {
        // Load inspector info from draft when resuming
        setInspectorInfo({
          inspector_id: draftData.inspector_id,
          inspector_name: draftData.inspector_name,
          inspector_code: draftData.inspector_code
        });
      }

      // Fetch template, clients, and standards in parallel
      const [templateResponse, clientsResponse, standardsResponse] = await Promise.all([
        formService.getTemplateFields(templateId),
        formService.getClients(),
        standardsService.getAllStandards()
      ]);

      // Set dynamic dropdown options
      if (standardsResponse && standardsResponse.success) {
        setDynamicOptions(standardsResponse);
      }

      if (templateResponse.success) {
        setTemplate(templateResponse.template);
        setFields(templateResponse.fields);

        // equipment_name / asset_unit_name mirror the selected equipment type; owner_name mirrors
        // the selected client. Looked up once here and reused for both the draft and fresh-form paths.
        const equipmentNameField = templateResponse.fields.find(f => f.field_name === 'equipment_name');
        const assetUnitField = templateResponse.fields.find(f => f.field_name === 'asset_unit_name');
        const ownerField = templateResponse.fields.find(f => f.field_name === 'owner_name');

        // Check if loading from draft
        if (draftData) {
          // Load draft data
          const draftInitialData = { ...(draftData.draft_data || {}) };
          if (equipmentType?.equipment_name) {
            if (equipmentNameField) draftInitialData[equipmentNameField.field_id] = equipmentType.equipment_name;
            if (assetUnitField) draftInitialData[assetUnitField.field_id] = equipmentType.equipment_name;
          }
          if (ownerField && draftData.client_id && clientsResponse.success) {
            const draftClient = clientsResponse.clients.find(c => String(c.client_id) === String(draftData.client_id));
            if (draftClient) draftInitialData[ownerField.field_id] = draftClient.client_name;
          }
          setFormData(draftInitialData);
          setSelectedClientId(draftData.client_id || '');
          setInspectionDate(draftData.inspection_date || new Date().toISOString().split('T')[0]);
          setCurrentDraftId(draftData.draft_id);
          console.log('Loaded draft data:', draftData);
        } else {
          // Initialize form data with default values
          const initialData = {};
          templateResponse.fields.forEach((field) => {
            const defaultValue = getDefaultValue(field);
            if (defaultValue !== null) {
              initialData[field.field_id] = defaultValue;
            }
            // Debug: Log checkbox defaults
            if (field.field_type === 'checkbox') {
              console.log(`Checkbox field ${field.field_label}:`, defaultValue);
            }
          });
          // Auto-set next inspection due date on initial load
          const dateOfInspField = templateResponse.fields.find(f => f.field_name === 'date_of_inspection');
          const dueDateField = templateResponse.fields.find(f => DUE_DATE_FIELD_NAMES.includes(f.field_name));
          const inspDate = initialData[dateOfInspField?.field_id] || new Date().toISOString().split('T')[0];
          if (dateOfInspField && dueDateField) {
            initialData[dueDateField.field_id] = calculateNextInspectionDate(inspDate, isAccessoriesEquipment(equipmentType));
          }
          // Auto-set colour_code on initial load
          const colourField = templateResponse.fields.find(f => f.field_name === 'colour_code');
          if (colourField) {
            initialData[colourField.field_id] = getAdnocColourCode(inspDate);
          }

          // Auto-set equipment_name / asset_unit_name from the selected equipment type
          if (equipmentType?.equipment_name) {
            if (equipmentNameField) initialData[equipmentNameField.field_id] = equipmentType.equipment_name;
            if (assetUnitField) initialData[assetUnitField.field_id] = equipmentType.equipment_name;
          }

          setFormData(initialData);
          console.log('Initial form data with defaults:', initialData);

          // Set inspection date to current date
          setInspectionDate(new Date().toISOString().split('T')[0]);
        }
      }

      if (clientsResponse.success) {
        setClients(clientsResponse.clients);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load template and clients');
      setLoading(false);
    }
  };

  const handleInputChange = (fieldId, value, fieldType) => {
    if (fieldType === 'checkbox') {
      const raw = formData[fieldId];
      // Ensure currentValues is always an array (guard against string values from pre-fill)
      const currentValues = Array.isArray(raw)
        ? raw
        : (typeof raw === 'string' && raw ? raw.split(',').map(v => v.trim()).filter(v => v) : []);
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      setFormData({ ...formData, [fieldId]: newValues });
    } else {
      const updatedData = { ...formData, [fieldId]: value };

      // Auto-update next inspection due date and colour_code when date_of_inspection changes
      const changedField = fields.find(f => f.field_id === fieldId);

      // If work_order_no is changed away from what was copied, clear replacement cert fields
      // so the replacement notice doesn't appear on a genuinely new work order
      if (changedField && changedField.field_name === 'work_order_no') {
        if (copyWorkOrderNo && value.trim() !== copyWorkOrderNo.trim()) {
          const oldCertField = fields.find(f => f.field_name === 'old_certificate_number');
          const oldDateField = fields.find(f => f.field_name === 'old_certificate_date');
          if (oldCertField) updatedData[oldCertField.field_id] = '';
          if (oldDateField) updatedData[oldDateField.field_id] = '';
        }
      }

      if (changedField && changedField.field_name === 'date_of_inspection') {
        const dueDateField = fields.find(f => DUE_DATE_FIELD_NAMES.includes(f.field_name));
        if (dueDateField) {
          updatedData[dueDateField.field_id] = calculateNextInspectionDate(value, isAccessoriesEquipment(equipmentType));
        }
        const colourField = fields.find(f => f.field_name === 'colour_code');
        if (colourField) {
          updatedData[colourField.field_id] = getAdnocColourCode(value);
        }
      }

      // Default "summary" to the option at the same index as the selected "type_of_inspection"
      // option — the two dropdowns' options are authored in matching order. Inspector can still
      // override the summary afterward.
      if (changedField && changedField.field_name === 'type_of_inspection') {
        const summaryField = fields.find(f => f.field_name === 'summary');
        if (summaryField) {
          const typeOptions = typeof changedField.field_options === 'string'
            ? JSON.parse(changedField.field_options)
            : (changedField.field_options || []);
          const summaryOptions = typeof summaryField.field_options === 'string'
            ? JSON.parse(summaryField.field_options)
            : (summaryField.field_options || []);
          const selectedIndex = typeOptions.findIndex(opt => (typeof opt === 'object' ? opt.value : opt) === value);
          if (selectedIndex !== -1 && summaryOptions[selectedIndex]) {
            const summaryOption = summaryOptions[selectedIndex];
            updatedData[summaryField.field_id] = typeof summaryOption === 'object' ? summaryOption.value : summaryOption;
            setSelectDisplayIndex(prev => ({ ...prev, [summaryField.field_id]: selectedIndex }));
          }
        }
      }

      setFormData(updatedData);

      // Special handling: If this is the "immediate danger" field and value changes
      // Clear the danger date field if the answer is not "yes"
      const field = changedField || fields.find(f => f.field_id === fieldId);
      if (field) {
        const label = (field.field_label || '').toLowerCase();
        if (label.includes('immediate') && label.includes('danger') && !label.includes('date')) {
          // This is the immediate danger field
          if (value.toLowerCase() !== 'yes') {
            // Clear the danger date field
            const dangerDateField = fields.find(f => {
              const dateLabel = (f.field_label || '').toLowerCase();
              return dateLabel.includes('danger') && dateLabel.includes('date');
            });
            if (dangerDateField) {
              setFormData(prev => ({ ...prev, [dangerDateField.field_id]: '' }));
            }
          }
        }
      }
    }

    // Clear error for this field
    if (errors[fieldId]) {
      setErrors({ ...errors, [fieldId]: '' });
    }
  };

  const validateField = (field, value) => {
    // Check if field is required
    if (field.is_required) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return `${field.field_label} is required`;
      }
    }

    // Check validation rules if they exist
    if (field.validation_rules) {
      try {
        const rules = JSON.parse(field.validation_rules);

        // Min length
        if (rules.min_length && value.length < rules.min_length) {
          return `${field.field_label} must be at least ${rules.min_length} characters`;
        }

        // Max length
        if (rules.max_length && value.length > rules.max_length) {
          return `${field.field_label} must not exceed ${rules.max_length} characters`;
        }

        // Min value (for numbers)
        if (rules.min_value && parseFloat(value) < parseFloat(rules.min_value)) {
          return `${field.field_label} must be at least ${rules.min_value}`;
        }

        // Max value (for numbers)
        if (rules.max_value && parseFloat(value) > parseFloat(rules.max_value)) {
          return `${field.field_label} must not exceed ${rules.max_value}`;
        }

        // Pattern (regex)
        if (rules.pattern) {
          const regex = new RegExp(rules.pattern);
          if (!regex.test(value)) {
            return rules.pattern_message || `${field.field_label} format is invalid`;
          }
        }
      } catch (e) {
        console.error('Error parsing validation rules:', e);
      }
    }

    return '';
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate client selection
    if (!selectedClientId) {
      newErrors.client = 'Please select a client';
    }

    // Validate inspection date
    if (!inspectionDate) {
      newErrors.inspection_date = 'Please select inspection date';
    }

    // Validate all fields (skip static_text)
    fields.forEach((field) => {
      // Skip validation for static_text fields
      if (field.field_type === 'static_text') {
        return;
      }
      // Skip validation for proof-load-only fields when this inspection type has no load test
      if (isProofLoadOnlyField(field) && !isProofLoadTestNeeded()) {
        return;
      }
      const value = formData[field.field_id];
      const error = validateField(field, value);
      if (error) {
        newErrors[field.field_id] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix the errors in the form');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      // Convert form data to proper format
      const submissionData = {
        template_id: templateId,
        client_id: parseInt(selectedClientId),
        inspection_date: inspectionDate,
        form_data: {}
      };

      // Add actual_inspector_id from selected inspector (from NewInspectionForm)
      if (inspectorInfo && inspectorInfo.inspector_id) {
        submissionData.actual_inspector_id = parseInt(inspectorInfo.inspector_id);
      }

      // Convert array values to comma-separated strings for checkboxes
      Object.keys(formData).forEach((fieldId) => {
        const value = formData[fieldId];
        if (Array.isArray(value)) {
          submissionData.form_data[fieldId] = value.join(', ');
        } else {
          submissionData.form_data[fieldId] = value;
        }
      });

      const response = await formService.submitForm(submissionData);

      if (response.success) {
        setSuccess(`Form submitted successfully! Form Number: ${response.submission.form_number}`);

        // If this was a draft, delete it after successful submission
        if (currentDraftId) {
          try {
            await draftService.deleteDraft(currentDraftId);
            console.log('Draft deleted after submission');
          } catch (err) {
            console.error('Failed to delete draft after submission:', err);
            // Don't show error to user since submission was successful
          }
        }

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate(`/dashboard/${user?.role}`);
        }, 2000);
      }

      setSubmitting(false);
    } catch (err) {
      setError(err.message || 'Failed to submit form');
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Save Draft Handler
  const handleSaveDraft = async () => {
    try {
      setSavingDraft(true);
      setError('');
      setSuccess('');

      const draftPayload = {
        template_id: templateId,
        form_type_id: formType.form_type_id,
        equipment_type_id: equipmentType.equipment_type_id,
        client_id: selectedClientId ? parseInt(selectedClientId) : null,
        inspection_date: inspectionDate || null,
        draft_data: formData,
        draft_id: currentDraftId, // Include if updating existing draft
        actual_inspector_id: inspectorInfo?.inspector_id || null // Save selected inspector
      };

      const response = await draftService.saveDraft(draftPayload);

      if (response.success) {
        const successMsg = response.message || 'Draft saved successfully!';
        setSuccess(successMsg);
        setCurrentDraftId(response.draft.draft_id); // Update draft ID

        // Show alert dialog
        alert(`✅ ${successMsg}\n\nYou can resume this draft later from "My Drafts" page.`);

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });

        setTimeout(() => setSuccess(''), 10000);
      }

      setSavingDraft(false);
    } catch (err) {
      setError(err.message || 'Failed to save draft');
      setSavingDraft(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Import Excel Handler
  const handleImportExcel = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      setError('');

      const response = await excelService.importExcel(file, templateId);

      if (response.success) {
        setImportSummary(response.summary);
        setImportedData(response.field_data);
        setShowImportModal(true);
      }

      setImporting(false);
    } catch (err) {
      setError(err.message || 'Failed to import Excel file');
      setImporting(false);
    }

    // Reset file input
    e.target.value = '';
  };

  const handleApplyImport = () => {
    if (importedData) {
      // Merge imported data with existing form data
      setFormData({ ...formData, ...importedData });
      setShowImportModal(false);
      setSuccess(`Successfully imported ${importSummary.filled_fields} fields from Excel!`);
      setTimeout(() => setSuccess(''), 5000);
    }
  };

  // Copy data from a previous work order
  const handleCopyFromWorkOrder = async () => {
    if (!copyWorkOrderNo.trim()) {
      setError('Please enter a work order number');
      return;
    }

    try {
      setCopyingFromWorkOrder(true);
      setError('');

      const response = await formService.copyFromWorkOrder(copyWorkOrderNo.trim());

      if (response.success && response.field_data) {
        const fetchedData = response.field_data;
        const newFormData = { ...formData };
        let matchedCount = 0;

        // These fields are handled specially below — skip them in the generic copy loop
        const SKIP_ON_COPY = ['old_certificate_number', 'old_certificate_date'];

        // Map fetched field_name values to current template's field_ids
        fields.forEach((field) => {
          if (SKIP_ON_COPY.includes(field.field_name)) return;
          if (fetchedData[field.field_name] !== undefined) {
            if (field.field_type === 'checkbox') {
              // Backend stores checkboxes as comma-separated strings; convert to array
              const val = fetchedData[field.field_name];
              newFormData[field.field_id] = val ? val.split(',').map(v => v.trim()).filter(v => v) : [];
            } else {
              newFormData[field.field_id] = fetchedData[field.field_name];
            }
            matchedCount++;
          }
        });

        // old_certificate_number / old_certificate_date are set ONLY when copying from the
        // SAME work order as the current form (i.e. it's a genuine revision/correction).
        // If the form's work_order_no is already set to a DIFFERENT value the user is just
        // using Copy to pre-fill a new work order — no replacement notice in that case.
        const workOrderNoField = fields.find(f => f.field_name === 'work_order_no');
        const currentWorkOrderNo = workOrderNoField ? (formData[workOrderNoField.field_id] || '').trim() : '';
        const isSameWorkOrder = !currentWorkOrderNo || currentWorkOrderNo === copyWorkOrderNo.trim();

        const prevCertNumber = fetchedData['certificate_number'] || response.form_number || '';
        const prevInspDate = response.inspection_date || fetchedData['date_of_inspection'] || '';

        const oldCertField = fields.find(f => f.field_name === 'old_certificate_number');
        const oldDateField = fields.find(f => f.field_name === 'old_certificate_date');
        if (isSameWorkOrder) {
          // Revision: populate replacement fields so the PDF notice is shown
          if (oldCertField) newFormData[oldCertField.field_id] = prevCertNumber;
          if (oldDateField) newFormData[oldDateField.field_id] = prevInspDate;
        } else {
          // Different work order — clear replacement fields
          if (oldCertField) newFormData[oldCertField.field_id] = '';
          if (oldDateField) newFormData[oldDateField.field_id] = '';
        }

        // equipment_name / asset_unit_name always mirror the CURRENT equipment type, not whatever
        // was stored on the copied-from submission. owner_name mirrors the CURRENT client selection
        // (which may change below), not the copied submission's original client.
        const equipmentNameField = fields.find(f => f.field_name === 'equipment_name');
        const assetUnitField = fields.find(f => f.field_name === 'asset_unit_name');
        if (equipmentType?.equipment_name) {
          if (equipmentNameField) newFormData[equipmentNameField.field_id] = equipmentType.equipment_name;
          if (assetUnitField) newFormData[assetUnitField.field_id] = equipmentType.equipment_name;
        }
        const ownerField = fields.find(f => f.field_name === 'owner_name');
        if (ownerField) {
          const copiedClient = response.client_id
            ? clients.find(c => String(c.client_id) === String(response.client_id))
            : null;
          newFormData[ownerField.field_id] = copiedClient ? copiedClient.client_name : '';
        }

        setFormData(newFormData);

        // Set client if available
        if (response.client_id) {
          setSelectedClientId(String(response.client_id));
        }

        setSuccess(`Copied ${matchedCount} fields from work order "${copyWorkOrderNo}" (Form: ${response.form_number}). You can edit any values before submitting.`);
        setTimeout(() => setSuccess(''), 8000);
      }

      setCopyingFromWorkOrder(false);
    } catch (err) {
      setError(err.message || 'Failed to fetch data from work order');
      setCopyingFromWorkOrder(false);
    }
  };

  // Sections that only apply when the certificate actually includes a Proof Load Test page.
  // Different template families were built independently over time and named these sections
  // differently, so this covers every variant found across the catalog (2026-07):
  const PROOF_LOAD_ONLY_SECTIONS = [
    // 21 accessory templates (BC, BT, CB, CS, EB, FA, FBHL, GW, HMPE, HK, HJR, JW, LB, PC, PB,
    // PBSM, TB-RS, SHK, TA, WS, LG) + CCCC
    'Detailed Record', 'Identification Numbers', 'Capacity', 'Testing History', 'Test Description',
    'Load Test Data', 'NDT Results', 'Defects', 'Page 2 Certification',
    // Mobile crane group: MC, PC14C, PC15C, CC, BTC, JC, TMC (NOT their separate RCI/SLI sections)
    'Load Test Details', 'Load Test Results',
    // MEWP, Overhead Crane, Floor Crane, Pad Eye, Gangway, Lifting Fork, Pallet Fork, Launch
    // Recovery System
    'Load Test Certificate',
    // Forklift Truck, Reach Stacker, Pallet Truck
    'Proof Load Test',
    // Winch/Runway Beam group: Winch (Pneumatic/Electric/Hydraulic), Winches
    // (Towing/Anchor/Mooring), Winch - Man Riding, Anchor Windlass, Runway Beam (+ w/ Hoist &
    // Trolley) — NOT "Page 2 Findings", which is normal always-visible content
    'Page 3 Record', 'Page 3 Identification', 'Page 3 Testing History', 'Page 3 Test Details',
    'Page 3 Load Test', 'Page 3 Load Test Details', 'Page 3 Defects', 'Page 3 Certification',
    'Page 3 Main Hoist', 'Page 3 Aux Hoist',
  ];

  // "next_proof_load_test_due" is excluded ONLY in "Page 2 Certification" — verified (by reading
  // the actual .docx templates) that for the 21-accessory+CCCC group this field's value is shared
  // with the always-visible page-1 "Next Proof Load Test Due on" placeholder via the same
  // {{next_proof_load_test_due}} tag used twice. Other families (e.g. the winch/runway-beam
  // group's "Page 3 Certification") use a genuinely distinct field for their page-1 due date
  // (e.g. "next_proof_load_due"), so no such collision exists there and this field should disable
  // normally.
  const SHARED_NEXT_PROOF_LOAD_DUE_SECTIONS = ['Page 2 Certification'];

  const isProofLoadOnlyField = (field) => {
    if (!PROOF_LOAD_ONLY_SECTIONS.includes(field.field_section)) return false;
    if (field.field_name === 'next_proof_load_test_due' && SHARED_NEXT_PROOF_LOAD_DUE_SECTIONS.includes(field.field_section)) {
      return false;
    }
    return true;
  };

  const isProofLoadTestNeeded = () => {
    const typeField = fields.find(f => f.field_name === 'type_of_inspection');
    if (!typeField) return true;
    const value = formData[typeField.field_id];
    if (!value) return true;
    return !value.toLowerCase().includes('without');
  };

  const renderField = (field) => {
    // Hide these fields from the form UI — values are still stored in formData and sent to the
    // backend for PDF generation (replacement notice), but inspectors don't need to see them.
    if (['date_of_inspection', 'old_certificate_number', 'old_certificate_date'].includes(field.field_name)) return null;

    // Signature file fields are never uploaded through the form — the certificate PDF fills
    // inspector_signature/reviewer_signature automatically from the inspector/reviewer name
    // (see pdf_template_utils.py). witnessed_by_signature has no such auto-fill (external
    // client/TPI signatory) and is left blank on the certificate for physical signing.
    if (['inspector_signature', 'reviewer_signature', 'witnessed_by_signature'].includes(field.field_name)) return null;

    // Label overrides (fix DB labels without needing a SQL migration)
    const LABEL_OVERRIDES = {
      'certificate_number': 'Certificate Number',
      'old_certificate_number': 'Old Certificate Number',
      'old_certificate_date': 'Old Certificate Date',
    };
    const displayLabel = LABEL_OVERRIDES[field.field_name] || field.field_label;

    // These fields are always read-only — either auto-filled from the previous work order when
    // copying, or auto-filled from the selected equipment type / client elsewhere in this file.
    const isReadOnly = ['old_certificate_number', 'old_certificate_date', 'equipment_name', 'asset_unit_name', 'owner_name'].includes(field.field_name);

    // Proof-load-only fields are greyed out (not required) when this inspection type has no load test
    const isProofLoadDisabled = isProofLoadOnlyField(field) && !isProofLoadTestNeeded();

    const value = formData[field.field_id];
    const hasError = errors[field.field_id];

    // Check if this text field should be rendered as an editable dropdown (combobox)
    const dropdownConfig = DYNAMIC_DROPDOWN_FIELDS[field.field_name];
    if (field.field_type === 'text' && dropdownConfig) {
      const options = dynamicOptions[dropdownConfig.dataKey] || [];
      const datalistId = `datalist-${field.field_id}`;
      return (
        <Form.Group className="mb-3" key={field.field_id}>
          <Form.Label>
            {displayLabel}
            {field.is_required && <span className="text-danger"> *</span>}
          </Form.Label>
          <Form.Control
            type="text"
            list={datalistId}
            value={value || ''}
            onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
            placeholder={`Select or type ${displayLabel}...`}
            isInvalid={hasError}
          />
          <datalist id={datalistId}>
            {options.map((option) => (
              <option key={option.id} value={option[dropdownConfig.valueField]} />
            ))}
          </datalist>
          {field.help_text && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
          <Form.Text className="text-muted d-block">
            <i className="fas fa-info-circle me-1"></i>
            Select from list or type/edit your own value
          </Form.Text>
          {hasError && <Form.Control.Feedback type="invalid">{hasError}</Form.Control.Feedback>}
        </Form.Group>
      );
    }

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'url':
        return (
          <Form.Group className="mb-3" key={field.field_id}>
            <Form.Label>
              {displayLabel}
              {field.is_required && !isReadOnly && !isProofLoadDisabled && <span className="text-danger"> *</span>}
              {isReadOnly && <span className="text-muted ms-1" style={{fontSize:'0.8em'}}>(auto-filled)</span>}
            </Form.Label>
            <Form.Control
              type={field.field_type}
              placeholder={isReadOnly ? '' : (field.placeholder || '')}
              value={value || ''}
              onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
              readOnly={isReadOnly}
              disabled={isProofLoadDisabled}
              style={isReadOnly || isProofLoadDisabled ? {backgroundColor:'#f8f9fa', cursor:isReadOnly ? 'default' : 'not-allowed'} : {}}
              isInvalid={hasError}
            />
            {isProofLoadDisabled && (
              <Form.Text className="text-muted">Not applicable — this inspection type has no load test</Form.Text>
            )}
            {field.help_text && !isProofLoadDisabled && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
            {field.field_name === 'colour_code' && (() => {
              const inspDateField = fields.find(f => f.field_name === 'date_of_inspection');
              const inspDateValue = inspDateField ? formData[inspDateField.field_id] : null;
              const hint = getColourCodeHint(inspDateValue || new Date().toISOString().split('T')[0]);
              return hint ? (
                <Form.Text className="text-info d-block mt-1">
                  <i className="fas fa-palette me-1"></i>
                  <strong>Colour period:</strong> {hint}
                </Form.Text>
              ) : null;
            })()}
            {hasError && <Form.Control.Feedback type="invalid">{hasError}</Form.Control.Feedback>}
          </Form.Group>
        );

      case 'number':
        return (
          <Form.Group className="mb-3" key={field.field_id}>
            <Form.Label>
              {displayLabel}
              {field.is_required && <span className="text-danger"> *</span>}
            </Form.Label>
            <Form.Control
              type="number"
              placeholder={field.placeholder || ''}
              value={value}
              onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
              isInvalid={hasError}
            />
            {field.help_text && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
            {hasError && <Form.Control.Feedback type="invalid">{hasError}</Form.Control.Feedback>}
          </Form.Group>
        );

      case 'date':
        // Check if this is a conditional date field (e.g., danger date depends on immediate danger selection)
        const fieldLabel = (field.field_label || '').toLowerCase();
        const isDangerDateField = fieldLabel.includes('danger') && fieldLabel.includes('date');

        // Find the immediate danger field
        let isConditionallyDisabled = false;
        if (isDangerDateField) {
          const immediateDangerField = fields.find(f => {
            const label = (f.field_label || '').toLowerCase();
            return label.includes('immediate') && label.includes('danger') && !label.includes('date');
          });

          if (immediateDangerField) {
            const dangerValue = formData[immediateDangerField.field_id];
            // Disable if not selected or if value is not "yes"
            isConditionallyDisabled = !dangerValue || dangerValue.toLowerCase() !== 'yes';
          }
        }

        return (
          <Form.Group className="mb-3" key={field.field_id}>
            <Form.Label>
              {displayLabel}
              {field.is_required && !isReadOnly && !isProofLoadDisabled && <span className="text-danger"> *</span>}
              {isReadOnly && <span className="text-muted ms-1" style={{fontSize:'0.8em'}}>(auto-filled)</span>}
            </Form.Label>
            <Form.Control
              type="date"
              value={value || ''}
              onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
              disabled={isConditionallyDisabled || isProofLoadDisabled}
              readOnly={isReadOnly}
              style={isReadOnly || isProofLoadDisabled ? {backgroundColor:'#f8f9fa', cursor:isReadOnly ? 'default' : 'not-allowed'} : {}}
              isInvalid={hasError}
            />
            {isConditionallyDisabled && (
              <Form.Text className="text-muted">
                This field is only enabled when "Immediate Danger" is set to "Yes"
              </Form.Text>
            )}
            {isProofLoadDisabled && (
              <Form.Text className="text-muted">Not applicable — this inspection type has no load test</Form.Text>
            )}
            {field.help_text && !isConditionallyDisabled && !isReadOnly && !isProofLoadDisabled && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
            {hasError && <Form.Control.Feedback type="invalid">{hasError}</Form.Control.Feedback>}
          </Form.Group>
        );

      case 'textarea':
        return (
          <Form.Group className="mb-3" key={field.field_id}>
            <Form.Label>
              {field.field_label}
              {field.is_required && !isProofLoadDisabled && !isReadOnly && <span className="text-danger"> *</span>}
              {isReadOnly && <span className="text-muted ms-1" style={{fontSize:'0.8em'}}>(auto-filled)</span>}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={isReadOnly ? '' : (field.placeholder || '')}
              value={value || ''}
              onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
              readOnly={isReadOnly}
              disabled={isProofLoadDisabled}
              style={isReadOnly || isProofLoadDisabled ? {backgroundColor:'#f8f9fa', cursor:isReadOnly ? 'default' : 'not-allowed'} : {}}
              isInvalid={hasError}
            />
            {isProofLoadDisabled && (
              <Form.Text className="text-muted">Not applicable — this inspection type has no load test</Form.Text>
            )}
            {field.help_text && !isProofLoadDisabled && !isReadOnly && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
            {hasError && <Form.Control.Feedback type="invalid">{hasError}</Form.Control.Feedback>}
          </Form.Group>
        );

      case 'select':
        const options = typeof field.field_options === 'string'
          ? JSON.parse(field.field_options)
          : (field.field_options || []);
        // Options are keyed by index (not text value) so fields with duplicate-text options
        // (e.g. "summary") visually highlight the exact option chosen, not just the first
        // option that happens to share the same text.
        const currentSelectIndex = selectDisplayIndex[field.field_id] !== undefined
          ? selectDisplayIndex[field.field_id]
          : options.findIndex(option => (typeof option === 'object' ? option.value : option) === value);
        return (
          <Form.Group className="mb-3" key={field.field_id}>
            <Form.Label>
              {field.field_label}
              {field.is_required && <span className="text-danger"> *</span>}
            </Form.Label>
            <Form.Select
              value={currentSelectIndex >= 0 ? currentSelectIndex : ''}
              onChange={(e) => {
                const idx = e.target.value === '' ? -1 : parseInt(e.target.value, 10);
                const selectedOption = options[idx];
                const optionValue = selectedOption ? (typeof selectedOption === 'object' ? selectedOption.value : selectedOption) : '';
                setSelectDisplayIndex(prev => ({ ...prev, [field.field_id]: idx }));
                handleInputChange(field.field_id, optionValue, field.field_type);
              }}
              isInvalid={hasError}
            >
              <option value="">-- Select {field.field_label} --</option>
              {options.map((option, index) => {
                const optionLabel = typeof option === 'object' ? option.label : option;
                return (
                  <option key={index} value={index}>
                    {optionLabel}
                  </option>
                );
              })}
            </Form.Select>
            {field.help_text && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
            {hasError && <Form.Control.Feedback type="invalid">{hasError}</Form.Control.Feedback>}
          </Form.Group>
        );

      case 'radio':
        const radioOptions = typeof field.field_options === 'string'
          ? JSON.parse(field.field_options)
          : (field.field_options || []);
        return (
          <Form.Group className="mb-3" key={field.field_id}>
            <Form.Label>
              {field.field_label}
              {field.is_required && <span className="text-danger"> *</span>}
            </Form.Label>
            <div>
              {radioOptions.map((option, index) => {
                const optionValue = typeof option === 'object' ? option.value : option;
                const optionLabel = typeof option === 'object' ? option.label : option;
                return (
                  <Form.Check
                    key={index}
                    type="radio"
                    id={`${field.field_id}-${index}`}
                    label={optionLabel}
                    name={`field-${field.field_id}`}
                    value={optionValue}
                    checked={value === optionValue}
                    onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
                    isInvalid={hasError}
                  />
                );
              })}
            </div>
            {field.help_text && <Form.Text className="text-muted">{field.help_text}</Form.Text>}
            {hasError && <div className="text-danger small">{hasError}</div>}
          </Form.Group>
        );

      case 'checkbox':
        const checkboxOptions = typeof field.field_options === 'string'
          ? JSON.parse(field.field_options)
          : (field.field_options || []);

        // Ensure value is always an array for checkboxes
        const checkboxValue = Array.isArray(value) ? value : [];

        // Debug logging
        console.log(`Rendering checkbox ${field.field_label}:`, checkboxValue);

        return (
          <Form.Group className="mb-3" key={field.field_id}>
            {/* <Form.Label className="d-block">
              {field.field_label}
              {field.is_required && <span className="text-danger"> *</span>}
            </Form.Label> */}
            {checkboxOptions.map((option, index) => {
              const optionValue = typeof option === 'object' ? option.value : option;
              // Use label if available, otherwise use value
              const optionLabel = typeof option === 'object' && option.label ? option.label : optionValue;
              const isChecked = checkboxValue.includes(optionValue);

              // Debug each checkbox
              console.log(`  Option ${optionLabel}: value=${optionValue}, checked=${isChecked}`);

              return (
                <div key={index} className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`${field.field_id}-${index}`}
                    value={optionValue}
                    checked={isChecked}
                    onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
                  />
                  <label className="form-check-label" htmlFor={`${field.field_id}-${index}`}>
                    {optionLabel}
                  </label>
                </div>
              );
            })}
            {field.help_text && <Form.Text className="text-muted d-block">{field.help_text}</Form.Text>}
            {hasError && <div className="text-danger small">{hasError}</div>}
          </Form.Group>
        );

      case 'static_text':
        // Read-only text display (no input) - Render HTML content
        return (
          <div className="mb-3" key={field.field_id}>
            {field.field_label && (
              <div className="fw-bold text-secondary mb-2">{field.field_label}</div>
            )}
            <div className="p-3 bg-light rounded border">
              <div
                className="mb-0"
                style={{ whiteSpace: 'pre-wrap' }}
                dangerouslySetInnerHTML={{ __html: field.placeholder_text || field.field_label }}
              />
            </div>
            {field.help_text && <Form.Text className="text-muted d-block mt-1">{field.help_text}</Form.Text>}
          </div>
        );

      case 'file':
        return (
          <PhotoFieldUpload
            key={field.field_id}
            field={field}
            value={value}
            disabled={isProofLoadDisabled}
            onChange={(filename) => handleInputChange(field.field_id, filename, field.field_type)}
          />
        );

      case 'table_rows':
        return (
          <DynamicRowsField
            key={field.field_id}
            field={field}
            value={value}
            disabled={isProofLoadDisabled}
            onChange={(jsonValue) => handleInputChange(field.field_id, jsonValue, field.field_type)}
          />
        );

      default:
        return null;
    }
  };

  // Group fields by section
  const groupFieldsBySection = () => {
    const sections = {};
    fields.forEach((field) => {
      const section = field.field_section || 'General';
      if (!sections[section]) {
        sections[section] = [];
      }
      sections[section].push(field);
    });
    return sections;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Container className="mt-5 text-center" style={{ paddingTop: '80px' }}>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading inspection form...</p>
        </Container>
      </>
    );
  }

  const sections = groupFieldsBySection();

  return (
    <>
      <Navbar />
      <Container className="mt-4 mb-5" style={{ paddingTop: '40px', maxWidth: '1000px' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold text-primary">
                  <i className="fas fa-clipboard-list me-2"></i>
                  Inspection Form
                </h2>
                <p className="text-muted mb-2">
                  <Badge bg="primary" className="me-2">{formType?.type_name}</Badge>
                  <Badge bg="success">{equipmentType?.equipment_name}</Badge>
                </p>
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  onClick={handleImportExcel}
                  disabled={importing || submitting || savingDraft}
                >
                  {importing ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-excel me-2"></i>
                      Import from Excel
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/new-inspection')}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Selection
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Copy from Previous Work Order */}
        <Row className="mb-3">
          <Col>
            <Card className="shadow-sm border-warning">
              <Card.Body className="py-2 px-3">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-copy text-warning"></i>
                  <span className="fw-bold text-muted" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    Copy from Previous Work Order:
                  </span>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Enter work order number..."
                    value={copyWorkOrderNo}
                    onChange={(e) => setCopyWorkOrderNo(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCopyFromWorkOrder(); } }}
                    style={{ maxWidth: '250px' }}
                    disabled={copyingFromWorkOrder}
                  />
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={handleCopyFromWorkOrder}
                    disabled={copyingFromWorkOrder || !copyWorkOrderNo.trim()}
                  >
                    {copyingFromWorkOrder ? (
                      <><Spinner animation="border" size="sm" className="me-1" />Fetching...</>
                    ) : (
                      <><i className="fas fa-download me-1"></i>Fetch Data</>
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Success Alert */}
        {success && (
          <Row className="mb-3">
            <Col>
              <Alert variant="success">
                <i className="fas fa-check-circle me-2"></i>
                {success}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Error Alert */}
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
              </Alert>
            </Col>
          </Row>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Basic Information Card */}
          <Row className="mb-4">
            <Col>
              <Card className="shadow">
                <Card.Header className="bg-info text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Basic Information
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Select Client <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={selectedClientId}
                          onChange={(e) => {
                            const newClientId = e.target.value;
                            setSelectedClientId(newClientId);
                            if (errors.client) setErrors({ ...errors, client: '' });

                            // Mirror the selected client's name into owner_name (read-only, auto-filled)
                            const ownerField = fields.find(f => f.field_name === 'owner_name');
                            if (ownerField) {
                              const client = clients.find(c => String(c.client_id) === String(newClientId));
                              setFormData(prev => ({ ...prev, [ownerField.field_id]: client ? client.client_name : '' }));
                            }
                          }}
                          isInvalid={errors.client}
                        >
                          <option value="">-- Select Client --</option>
                          {clients.map((client) => (
                            <option key={client.client_id} value={client.client_id}>
                              {client.client_name}
                            </option>
                          ))}
                        </Form.Select>
                        {errors.client && (
                          <Form.Control.Feedback type="invalid">{errors.client}</Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Inspection Date <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={inspectionDate}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            setInspectionDate(newDate);
                            if (errors.inspection_date) setErrors({ ...errors, inspection_date: '' });
                            // Auto-update date_of_inspection, due date, and colour_code in formData
                            const dateOfInspField = fields.find(f => f.field_name === 'date_of_inspection');
                            const dueDateField = fields.find(f => DUE_DATE_FIELD_NAMES.includes(f.field_name));
                            const colourField = fields.find(f => f.field_name === 'colour_code');
                            if (dateOfInspField || dueDateField || colourField) {
                              const updatedData = { ...formData };
                              if (dateOfInspField) {
                                updatedData[dateOfInspField.field_id] = newDate;
                              }
                              if (dueDateField) {
                                updatedData[dueDateField.field_id] = calculateNextInspectionDate(newDate, isAccessoriesEquipment(equipmentType));
                              }
                              if (colourField) {
                                updatedData[colourField.field_id] = getAdnocColourCode(newDate);
                              }
                              setFormData(updatedData);
                            }
                          }}
                          isInvalid={errors.inspection_date}
                        />
                        {errors.inspection_date && (
                          <Form.Control.Feedback type="invalid">
                            {errors.inspection_date}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Inspector{' '}
                          <Badge bg="success" className="ms-1">
                            <i className="fas fa-check-circle me-1"></i>
                            Selected
                          </Badge>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={inspectorInfo ? `${inspectorInfo.inspector_name} (${inspectorInfo.inspector_code})` : 'Not selected'}
                          disabled
                          readOnly
                        />
                        <Form.Text className="text-muted">
                          <i className="fas fa-user-check me-1"></i>
                          Inspector selected in previous step
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Dynamic Sections */}
          {Object.keys(sections).map((sectionName) => (
            <Row className="mb-4" key={sectionName}>
              <Col>
                <Card className="shadow">
                  <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-list-ul me-2"></i>
                      {sectionName}
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    {sections[sectionName].map((field) => renderField(field))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ))}

          {/* Action Buttons */}
          <Row>
            <Col className="text-center">
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                {/* Save Draft Button */}
                <Button
                  variant="info"
                  size="lg"
                  onClick={handleSaveDraft}
                  disabled={savingDraft || submitting || importing}
                  className="shadow px-4"
                >
                  {savingDraft ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving Draft...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save as Draft
                    </>
                  )}
                </Button>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={submitting || savingDraft || importing}
                  className="shadow px-4"
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Submitting Form...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2"></i>
                      Submit Inspection Form
                    </>
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>

        {/* Hidden file input for Excel import */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
        />

        {/* Import Summary Modal */}
        <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-file-excel me-2 text-success"></i>
              Excel Import Summary
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {importSummary && (
              <div>
                <Alert variant="info">
                  <h5 className="mb-3">Import Statistics</h5>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <strong>Total Fields:</strong> {importSummary.total_fields}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Filled Fields:</strong> <span className="text-success">{importSummary.filled_fields}</span>
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Empty Fields:</strong> <span className="text-warning">{importSummary.empty_fields}</span>
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Fill Rate:</strong> <span className="text-primary">{importSummary.filled_percentage}%</span>
                    </div>
                  </div>
                </Alert>

                {importSummary.missing_required_fields && importSummary.missing_required_fields.length > 0 && (
                  <Alert variant="warning">
                    <h6>Missing Required Fields:</h6>
                    <ul className="mb-0">
                      {importSummary.missing_required_fields.map((field, index) => (
                        <li key={index}>{field}</li>
                      ))}
                    </ul>
                    <small className="text-muted mt-2 d-block">
                      You can fill these fields manually after importing.
                    </small>
                  </Alert>
                )}

                <div className="mt-3">
                  <p className="text-muted mb-0">
                    Click "Apply Import" to fill the form with the imported data. You can review and edit the fields before submitting.
                  </p>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleApplyImport}>
              <i className="fas fa-check me-2"></i>
              Apply Import
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default DynamicForm;
